import { AuditItems, CustomBottomBar, FileUploader, MinimalStepper } from '@components';
import { Button, Container, Dropdown, Icon, InputDropdownMulti, Typography } from '@design-system';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import './SecurityAuditPage.scss';
import { useSecurityAuditPageVM } from './useSecurityAuditPageVM';
import { AuditItem } from '@services/securityAudit/securityAudit.types';

export const SecurityAuditPage: React.FC = () => {
    const {
        audits,
        computedStepperSteps,
        worksitesOptions,
        operatorsOptions,
        selectedOperatorId,
        selectedWorksiteId,
        usersOptions,
        usersMet,
        handleSetUsersMet,
        onOperatorChange,
        onWorksiteChange,
        handleAuditItemChange,
        handleAuditItemCommentChange,
        setFiles,
        currentStep,
        isStepCompleted,
        canGoBack,
        handleNextStep,
        handlePrevStep,
        navigateToAudit,
        isLastStep,
        handleSendAudit
    } = useSecurityAuditPageVM();

    return (
        <div className="security-audit">
            <Container className="security-audit-header" radius="none" column>
                <Typography intrinsic="h2" className="mb-large-spacing">
                    {currentStep}
                </Typography>
                <MinimalStepper steps={computedStepperSteps} />
            </Container>

            <Container>
                {(() => {
                    const currentAudit = audits.find((item) => item.step === currentStep);
                    if (currentAudit && currentAudit.items && currentAudit.items.length > 0) {
                        return (
                            <>
                                <Container bordered="grey" className="audit-item-container" key={currentAudit.step}>
                                    <AuditItems
                                        title={currentAudit.step}
                                        description={currentAudit.description}
                                        items={currentAudit.items}
                                        onChange={(itemId, compliant) =>
                                            handleAuditItemChange(currentAudit.step, itemId, compliant as AuditItem['compliant'])
                                        }
                                        onCommentChange={(itemId, comment) =>
                                            handleAuditItemCommentChange(currentAudit.step, itemId, comment)
                                        }
                                    />
                                </Container>
                                <FileUploader files={currentAudit.files ?? []} setFiles={setFiles} overflow={false} />
                            </>
                        );
                    }

                    const currentAuditIndex = audits.findIndex((item) => item.step === currentStep);

                    if (currentAuditIndex === 0) {
                        return (
                            <Container bordered="grey" className="visit-infos-container">
                                <Typography intrinsic="h3" className="pl-small-spacing">
                                    Visite préventive
                                </Typography>

                                <Dropdown
                                    options={operatorsOptions}
                                    placeholder="Responsable"
                                    onOptionChange={(option) => onOperatorChange(option.id)}
                                    value={operatorsOptions.find((operator) => operator.id === selectedOperatorId)}
                                />

                                <Dropdown
                                    options={worksitesOptions}
                                    placeholder="Chantier"
                                    onOptionChange={(option) => onWorksiteChange(option.id)}
                                    value={
                                        selectedWorksiteId && worksitesOptions.length > 0
                                            ? worksitesOptions.find((worksite) => worksite.id === selectedWorksiteId) || null
                                            : null
                                    }
                                    isSearchable={true}
                                />

                                <InputDropdownMulti
                                    placeholder="Compagnons rencontrés"
                                    onOptionChange={(options) => handleSetUsersMet(options.map((option) => option.id))}
                                    value={usersMet.map(
                                        (user) => usersOptions.find((option) => option.id === user) ?? { id: user, label: user }
                                    )}
                                />
                                <Typography intrinsic="p" color="grey" defaultSpacing>
                                    Appuyez sur "Entrée" pour ajouter un compagnon
                                </Typography>
                            </Container>
                        );
                    }

                    return (
                        <>
                            {audits
                                .filter((_, index) => index !== 0 && index !== audits.length - 1)
                                .map((audit, index) => (
                                    <div key={audit.step}>
                                        <Container
                                            bordered="grey"
                                            className={`audit-item-container ${index === 0 ? '' : 'mt-large-spacing'}`}
                                        >
                                            <AuditItems
                                                key={audit.step}
                                                title={audit.step}
                                                description={audit.description}
                                                items={audit.items}
                                                onChange={(itemId, compliant) => handleAuditItemChange(audit.step, itemId, compliant)}
                                                onCommentChange={(itemId, comment) =>
                                                    handleAuditItemCommentChange(audit.step, itemId, comment)
                                                }
                                                disabled={true}
                                                callback={() => navigateToAudit(audit.step)}
                                            />
                                        </Container>
                                        {audit.files && audit.files.length > 0 && (
                                            <FileUploader files={audit.files} setFiles={setFiles} overflow={false} disabled />
                                        )}
                                    </div>
                                ))}
                        </>
                    );
                })()}
            </Container>

            <CustomBottomBar>
                <Button
                    color="marine"
                    variant="secondary"
                    onClick={handlePrevStep}
                    disabled={!canGoBack}
                    className="sm-bottom-bar-button mr-small-spacing"
                >
                    <Icon className="arrow-left" color="grey" icon={<LiaArrowLeftSolid />} size={30} />
                </Button>
                <Button
                    variant="primary"
                    color="marine"
                    size="fullwidth"
                    disabled={!isStepCompleted}
                    onClick={!isLastStep ? handleNextStep : handleSendAudit}
                    className="next-button"
                >
                    <Typography intrinsic="h3">{isLastStep ? "Envoyer l'audit" : 'Suivant'}</Typography>
                </Button>
            </CustomBottomBar>
        </div>
    );
};
